import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import { ContentLanguageHeaderName } from "@/api/ApiClientProvider";
import { useApi } from "@/api/hooks/useApi";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import type { ApiQueryParams } from "@/types/api-types";
import type { EntityDetailsQueryOptions } from "@/types/query-types";

export const useEventQueries = () => {
  const api = useApi();
  const projectId = useProjectId();

  const limit = 12;

  return {
    listInfinite: (query: ApiQueryParams<"getEventsV1">) =>
      infiniteQueryOptions({
        queryKey: QUERY_KEYS.EVENT_LIST(projectId, query),
        queryFn: ({ pageParam }) =>
          api
            .getEventsV1({ ...query, Offset: pageParam * limit, Limit: limit })
            .then((items) => commonAPIDataSelector(items)),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => (lastPage?.hasMore ? pages?.length : undefined),
      }),
    details: (
      {
        eventId,
        query,
      }: {
        eventId: string;
        query?: ApiQueryParams<"getEventsDetailsV1", 1>;
      },
      options?: EntityDetailsQueryOptions,
    ) => {
      const headers = {
        [ContentLanguageHeaderName]: options?.translationMode || "default",
      };

      return queryOptions({
        queryKey: QUERY_KEYS.EVENT_DETAILS(projectId, eventId, { ...query, ...options }),
        queryFn: () => api.getEventsDetailsV1(eventId, query, { headers }),
        select: commonAPIDataSelector,
      });
    },
    listCategories: () =>
      queryOptions({
        queryKey: QUERY_KEYS.EVENT_CATEGORIES(projectId),
        queryFn: () => api.getEventsCategoriesV1(),
        select: commonAPIDataSelector,
      }),
    participantCandidatesInfinite: (eventId: string, search: string) =>
      infiniteQueryOptions({
        queryKey: QUERY_KEYS.EVENT_PARTICIPANT_CANDIDATES(projectId, eventId, { search }),
        queryFn: ({ pageParam }) =>
          api
            .getEventsParticipantCandidatesV1(eventId, {
              search: search || undefined,
              Offset: pageParam * 25,
              Limit: 25,
            })
            .then(commonAPIDataSelector),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => (lastPage.hasMore ? pages.length : undefined),
      }),
  };
};
